import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  floatingButton: {
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  }
}));

const MarkChatsAsReadModal = ({ modalOpen, onClose }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const [allowGroups, setAllowGroups] = useState("disabled");
  const [loading, setLoading] = useState(false);
  const [whatsappsArray, setWhatsappsArray] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");

  // Type of Tickets:
  //
  //  0 - all
  //  1 - groups
  //  2 - pending tickets
  //  3 - open tickets
  //
  const typeOfTicketsDictionary = {
    0: i18n.t("markChatsAsReadModal.form.selectOptions.all"),
    1: i18n.t("markChatsAsReadModal.form.selectOptions.groups"),
    2: i18n.t("markChatsAsReadModal.form.selectOptions.pending"),
    3: i18n.t("markChatsAsReadModal.form.selectOptions.open"),
  };
  const [typeOfTickets, setTypeOfTickets] = useState(3);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    const fetchWhatsappsArray = async () => {
      try {
        setLoading(true);

        const apiCalls = [api.get("/whatsapp/")];
        const [WhatsappResponse] = await Promise.all(apiCalls);
        const defaultWhatsapp = WhatsappResponse.data.filter(whatsapp => whatsapp.isDefault)[0];

        setWhatsappsArray(WhatsappResponse.data);
        setWhatsappId(whatsappId === "" && defaultWhatsapp ? defaultWhatsapp.id : whatsappId);
        setLoading(false);
      } catch (exception) {
        setLoading(false);
        console.log("Exception caught while fetching Whatsapps - MarkChastAsReadModal Use Effect:", exception);
        toastError(exception);
      }
    };

    const fetchSettings = async () => {
      try {
        setLoading(true);

        const apiCalls = [api.get("/settings/allowGroups")];
        const [AllowGroupsResponse] = await Promise.all(apiCalls);
        
        setAllowGroups(AllowGroupsResponse.data.value); // getting "allowGroups" setting
        setLoading(false);
      } catch (exception) {
        setLoading(false);
        console.log("Exception caught while fetching Settings - MarkChastAsReadModal Use Effect:", exception);
        toastError(exception);
      }
    };

    if (modalOpen) {
      fetchWhatsappsArray();
      fetchSettings();
    }
    else { setLoading(false); }
  }, [modalOpen, whatsappId]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
    setLoading(false);
    setWhatsappId("");
    setTypeOfTickets(3);
  };

  const handleMarkChatAsRead = async () => {
    try {
      if (whatsappId === null || whatsappId === undefined || whatsappId === "") {
        toast.info(i18n.t("markChatsAsReadModal.toasts.whatsappNotSelected"));
      }
      else if (typeOfTickets === null || typeOfTickets === undefined || typeOfTickets === "") {
        toast.info(i18n.t("markChatsAsReadModal.toasts.ticketTypeNotSelected"));
      }
      else {
        setLoading(true);

        const params = { whatsappId, typeOfTickets };
        await api.patch("/markTicketsAsRead", params);

        setLoading(false);
        handleClose();
      }
    } catch (exception) {
      setLoading(false);
      console.log("Exception caught when handling marking chats as read:", exception);
      toastError(exception);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Dialog open={modalOpen} onClose={handleClose}>
      <DialogTitle>
        {i18n.t("markChatsAsReadModal.title")}
      </DialogTitle>

      <DialogContent dividers>
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("markChatsAsReadModal.form.connection")}</InputLabel>

          <Select
            value={whatsappId}
            onChange={(event) => setWhatsappId(event.target.value)}
            label={i18n.t("markChatsAsReadModal.form.connection")}
            fullWidth
          >
            {/* ***---- Connected Chips ----*** */}
            <ListSubheader>{i18n.t("markChatsAsReadModal.form.listSubheaders.connected")}</ListSubheader>
            {whatsappsArray.map(whatsapp => {
              return whatsapp.status === "CONNECTED"
                ? <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
                : <></>;
            })}

            {/* ***---- Disconnected Chips ----*** */}
            <ListSubheader>{i18n.t("markChatsAsReadModal.form.listSubheaders.disconnected")}</ListSubheader>
            {whatsappsArray.map(whatsapp => {
              return whatsapp.status !== "CONNECTED"
                ? <MenuItem key={whatsapp.id} value={whatsapp.id} disabled>{whatsapp.name}</MenuItem>
                : <></>;
            })}
          </Select>
        </FormControl>

        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("markChatsAsReadModal.form.typeOfTicket")}</InputLabel>

          <Select
            value={typeOfTickets}
            onChange={(event) => setTypeOfTickets(event.target.value)}
            label={i18n.t("markChatsAsReadModal.form.typeOfTicket")}
            fullWidth
          >  
            {Object.entries(typeOfTicketsDictionary).map(([key, value]) => {
              const shouldDisplayOption = key !== "1" || (key === "1" && allowGroups === "enabled");

              return shouldDisplayOption
                ? <MenuItem value={parseInt(key, 10)}>{value}</MenuItem>
                : <></>;
            })}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button
          className={classes.floatingButton}
          onClick={onClose}
          color="inherit"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("markChatsAsReadModal.buttons.cancel")}
        </Button>

        <ButtonWithSpinner
          className={classes.floatingButton}
          variant="contained"
          type="button"
          loading={loading}
          color="primary"
          onClick={handleMarkChatAsRead}
        >
          {i18n.t("markChatsAsReadModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default MarkChatsAsReadModal;